import {
  DownloadOutlined,
  FileExcelOutlined,
  FileOutlined,
  FilePptOutlined,
  FileWordOutlined,
  MinusOutlined,
  PlusOutlined,
  UndoOutlined
} from "@ant-design/icons"
import { Alert, Button, Space, Spin } from "antd"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { downloadFile } from "../../utils/download-file.ts"
import { PdfViewer } from "./PdfViewer.tsx"

type FileType =
  | "image"
  | "video"
  | "audio"
  | "pdf"
  | "office"
  | "text"
  | "unknown"

const FILE_EXTENSIONS: Record<FileType, string[]> = {
  image: [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg", ".bmp", ".ico"],
  video: [".mp4", ".mov", ".webm", ".avi", ".mkv", ".m4v"],
  audio: [".mp3", ".wav", ".ogg", ".m4a", ".flac", ".aac"],
  pdf: [".pdf"],
  office: [".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".csv"],
  text: [".txt", ".json", ".xml", ".md", ".html", ".css", ".js", ".ts"],
  unknown: []
}

export const getFileType = (url: string, fileName?: string): FileType => {
  for (const candidate of [url, fileName]) {
    if (!candidate) continue
    const lower = candidate.toLowerCase()
    for (const [type, extensions] of Object.entries(FILE_EXTENSIONS)) {
      if (extensions.some((ext) => lower.includes(ext))) {
        return type as FileType
      }
    }
  }

  return "unknown"
}

// officeapps.live.com fetches the src server-side, so a blob: URL is unreachable to it
export const resolvePreviewType = (
  url: string,
  fileName?: string
): FileType => {
  const type = getFileType(url, fileName)
  if (type === "office" && !/^https?:/i.test(url)) return "unknown"
  return type
}

const getOfficeIcon = (url: string) => {
  const lowerUrl = url.toLowerCase()
  if (lowerUrl.includes(".doc"))
    return <FileWordOutlined style={{ fontSize: 64, color: "#2b579a" }} />
  if (lowerUrl.includes(".xls") || lowerUrl.includes(".csv"))
    return <FileExcelOutlined style={{ fontSize: 64, color: "#217346" }} />
  if (lowerUrl.includes(".ppt"))
    return <FilePptOutlined style={{ fontSize: 64, color: "#d24726" }} />
  return <FileOutlined style={{ fontSize: 64 }} />
}

const Loader = () => (
  <div
    style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 1
    }}
  >
    <Spin size="large" />
  </div>
)

const ZOOM_MIN = 1
const ZOOM_MAX = 3
const ZOOM_STEP = 0.5

interface FilePreviewContentProps {
  fileUrl: string
  name: string
  /** Caps preview height — the small modal uses 70vh, the full-screen viewer overrides it. */
  maxHeight?: string
  onRetry?: () => void
}

export function FilePreviewContent({
  fileUrl,
  name,
  maxHeight = "70vh",
  onRetry
}: FilePreviewContentProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [textContent, setTextContent] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)

  const fileType = useMemo(() => {
    if (!fileUrl) return "unknown"
    return resolvePreviewType(fileUrl, name)
  }, [fileUrl, name])

  useEffect(() => {
    setLoading(true)
    setError(null)
    setTextContent(null)
    setZoom(1)

    if (!fileUrl) return

    if (fileType === "text") {
      fetch(fileUrl)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch")
          return res.text()
        })
        .then((text) => {
          setTextContent(text)
          setLoading(false)
        })
        .catch(() => {
          setError("Failed to load text file")
          setLoading(false)
        })
    }
  }, [fileUrl, fileType])

  const handleLoad = () => setLoading(false)
  const handleError = (msg: string) => {
    setLoading(false)
    setError(msg)
  }

  const handleDownload = async () => {
    if (!fileUrl) return
    await downloadFile(fileUrl, name)
  }

  const handleRetry = () => {
    setError(null)
    setLoading(true)
    onRetry?.()
  }

  if (error) {
    return (
      <Alert
        message={t("filePreview.errorTitle")}
        description={error}
        type="error"
        showIcon
        action={
          <Space direction="vertical">
            <Button size="small" onClick={handleRetry}>
              {t("filePreview.tryAgain")}
            </Button>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleDownload}>
              {t("filePreview.downloadInstead")}
            </Button>
          </Space>
        }
      />
    )
  }

  switch (fileType) {
    case "image":
      return (
        <div
          style={{ textAlign: "center", position: "relative", height: "100%" }}
        >
          {loading && <Loader />}
          <div
            style={{
              height: "100%",
              overflow: zoom > 1 ? "auto" : "hidden",
              display: "flex",
              alignItems: zoom > 1 ? "flex-start" : "center",
              justifyContent: "center"
            }}
          >
            <img
              src={fileUrl}
              alt={name}
              style={{
                maxWidth: zoom > 1 ? "none" : "100%",
                maxHeight: zoom > 1 ? "none" : maxHeight,
                width: zoom > 1 ? `${zoom * 100}%` : undefined,
                objectFit: "contain",
                display: loading ? "none" : "block",
                margin: "0 auto"
              }}
              onLoad={handleLoad}
              onError={() => handleError("Failed to load image")}
            />
          </div>
          {!loading && (
            <Space
              className="ant-shadow"
              style={{
                position: "absolute",
                bottom: 12,
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(0,0,0,0.65)",
                borderRadius: 8,
                padding: "4px 8px"
              }}
            >
              <Button
                size="small"
                type="text"
                style={{ color: "#fff" }}
                icon={<MinusOutlined />}
                aria-label={t("filePreview.zoomOut")}
                disabled={zoom <= ZOOM_MIN}
                onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
              />
              <span style={{ color: "#fff", fontSize: 12, minWidth: 40, display: "inline-block", textAlign: "center" }}>
                {Math.round(zoom * 100)}%
              </span>
              <Button
                size="small"
                type="text"
                style={{ color: "#fff" }}
                icon={<PlusOutlined />}
                aria-label={t("filePreview.zoomIn")}
                disabled={zoom >= ZOOM_MAX}
                onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
              />
              <Button
                size="small"
                type="text"
                style={{ color: "#fff" }}
                icon={<UndoOutlined />}
                aria-label={t("filePreview.zoomReset")}
                disabled={zoom === 1}
                onClick={() => setZoom(1)}
              />
            </Space>
          )}
        </div>
      )

    case "video":
      return (
        <div style={{ textAlign: "center", position: "relative" }}>
          {loading && <Loader />}
          <video
            src={fileUrl}
            controls
            preload="metadata"
            style={{
              maxWidth: "100%",
              maxHeight,
              borderRadius: 6,
              display: loading ? "none" : "block",
              margin: "0 auto"
            }}
            onLoadedData={handleLoad}
            onError={() => handleError("Failed to load video")}
          />
        </div>
      )

    case "audio":
      return (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <audio
            src={fileUrl}
            controls
            style={{ width: "100%", maxWidth: 500 }}
            onLoadedData={handleLoad}
            onError={() => handleError("Failed to load audio")}
          />
        </div>
      )

    case "pdf":
      return <PdfViewer fileUrl={fileUrl} name={name} onLoad={handleLoad} />

    case "office":
      // The embed is a Microsoft-hosted page that fetches `fileUrl` on its
      // own server, outside this browser — its `onLoad` only confirms that
      // page itself loaded, not that it could reach and render our file
      // (it cannot reach a local dev URL at all, for instance). There is no
      // cross-origin way to tell success from a silent failure inside it,
      // so the fallback stays visible rather than only appearing on a
      // failure this code can't actually detect.
      return (
        <div>
          <div style={{ height: maxHeight, position: "relative" }}>
            {loading && <Loader />}
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: 6
              }}
              title={name}
              onLoad={handleLoad}
              onError={() => handleError("Failed to load document")}
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "12px 0 0",
              color: "#666"
            }}
          >
            <span>{t("filePreview.officeFallbackPrompt")}</span>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleDownload}>
              {t("filePreview.downloadInstead")}
            </Button>
          </div>
        </div>
      )

    case "text":
      return (
        <div style={{ position: "relative" }}>
          {loading && <Loader />}
          {textContent && (
            <pre
              style={{
                background: "#f5f5f5",
                padding: 16,
                borderRadius: 6,
                maxHeight,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                margin: 0,
                fontSize: 13
              }}
            >
              {textContent}
            </pre>
          )}
        </div>
      )

    default:
      return (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          {getOfficeIcon(fileUrl)}
          <p style={{ marginTop: 16, color: "#666" }}>
            {t("filePreview.notAvailable")}
          </p>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
            {t("filePreview.downloadFile")}
          </Button>
        </div>
      )
  }
}
